/**
 * Count item snapshots for reporting and closing counts.
 * snapshot_at_close is written when a session completes so later inventory edits
 * do not change historical count values.
 */

import { getPLCategoryMap, resolvePLCategoryFromMap } from './plCategoryResolver';

export function normalizeSnapshotPrices(snap) {
  if (!snap || typeof snap !== 'object') return {};
  const unit = snap.unit_price ?? snap.each_price ?? null;
  const each = snap.each_price ?? snap.unit_price ?? null;
  return {
    ...snap,
    unit_price: unit,
    each_price: each,
  };
}

/** Prefer frozen close snapshot for reports; fall back to live session snapshot. */
export function reportingSnapshotFromCountItem(row) {
  if (row?.snapshot_at_close && typeof row.snapshot_at_close === 'object') {
    return normalizeSnapshotPrices(row.snapshot_at_close);
  }
  return normalizeSnapshotPrices(row?.item_snapshot || {});
}

/**
 * Unit price for value calcs: the verified normalized_unit_cost when present
 * (finite > 0), else the legacy rule — case_price if set and > 0, else
 * each/unit price. Snapshots without a normalized cost value exactly as before.
 */
export function unitValuePriceFromSnapshot(snap) {
  const s = normalizeSnapshotPrices(snap);
  const normalized = parseFloat(s.normalized_unit_cost);
  if (Number.isFinite(normalized) && normalized > 0) return normalized;
  const caseP = parseFloat(s.case_price);
  if (Number.isFinite(caseP) && caseP > 0) return caseP;
  const each = parseFloat(s.each_price ?? s.unit_price);
  return Number.isFinite(each) ? each : 0;
}

export function unitValuePriceFromCountItem(row) {
  return unitValuePriceFromSnapshot(reportingSnapshotFromCountItem(row));
}

/**
 * Merge count row + current inventory into a row to store as snapshot_at_close.
 */
export function buildSnapshotAtClose(countItem, inventoryRow) {
  const prev =
    countItem.item_snapshot && typeof countItem.item_snapshot === 'object'
      ? { ...countItem.item_snapshot }
      : {};
  const inv = inventoryRow && typeof inventoryRow === 'object' ? inventoryRow : {};

  const caseQty = inv.case_quantity ?? prev.case_quantity ?? prev.case_qty ?? null;
  const splitQty = inv.split_quantity ?? prev.split_quantity ?? prev.split_qty ?? null;

  const merged = {
    ...prev,
    id: inv.id ?? prev.id,
    description: inv.description ?? prev.description,
    brand: inv.brand ?? prev.brand,
    category: inv.category ?? prev.category,
    location: countItem.location ?? inv.location ?? prev.location,
    vendor: inv.vendor ?? prev.vendor,
    unit: inv.unit ?? prev.unit,
    pack_size: inv.pack_size ?? prev.pack_size,
    case_quantity: caseQty,
    split_quantity: splitQty,
    case_qty: caseQty,
    split_qty: splitQty,
    case_price: inv.case_price ?? prev.case_price,
    unit_price: inv.unit_price ?? prev.unit_price,
    each_price: inv.each_price ?? inv.unit_price ?? prev.each_price ?? prev.unit_price,
    moq: inv.moq ?? prev.moq,
    moq_unit: inv.moq_unit ?? prev.moq_unit,
    manufacturer_code: inv.manufacturer_code ?? prev.manufacturer_code,
  };

  merged.each_price = merged.each_price ?? merged.unit_price;
  merged.unit_price = merged.unit_price ?? merged.each_price;

  // ── Verified-cost valuation fields (multi-vendor purchasing model) ──────
  // count_unit mirrors the unit resolution already used above (item.unit).
  merged.count_unit = merged.unit ?? null;

  // Legacy fallback resolved by the same price logic reports have always
  // used (case price first, then each/unit). A zero/blank price is treated
  // as no price — a missing cost is never silently stored as 0.
  const legacyCost = (() => {
    const caseP = parseFloat(merged.case_price);
    if (Number.isFinite(caseP) && caseP > 0) return caseP;
    const each = parseFloat(merged.each_price ?? merged.unit_price);
    return Number.isFinite(each) && each > 0 ? each : null;
  })();

  const verifiedCost = parseFloat(inv.verified_unit_cost);
  const hasVerifiedCost = Number.isFinite(verifiedCost) && verifiedCost > 0;

  merged.normalized_unit_cost = hasVerifiedCost ? verifiedCost : legacyCost;
  merged.price_source = hasVerifiedCost
    ? inv.verified_cost_source || 'verified'
    : legacyCost != null
      ? 'legacy_item_price'
      : 'missing';
  merged.price_source_date = inv.verified_cost_effective_date ?? inv.updated_at ?? null;
  if (inv.conversion_snapshot != null) {
    merged.conversion_snapshot = inv.conversion_snapshot;
  }

  const qty = parseFloat(countItem.actual_quantity);
  merged.extended_value =
    Number.isFinite(qty) && merged.normalized_unit_cost != null
      ? qty * merged.normalized_unit_cost
      : null;

  return merged;
}

/**
 * Persist immutable per-line snapshots for a completed session (idempotent).
 */
export async function freezeSnapshotsForCompletedSession(supabase, sessionId) {
  if (!supabase || !sessionId) return;

  const { data: session, error: sessErr } = await supabase
    .from('count_sessions')
    .select('id, status, store_id, voided_at')
    .eq('id', sessionId)
    .single();

  // Skip sessions that are not completed, or have been voided — voided sessions
  // are audit records only and their item rows should remain as-is.
  if (sessErr || !session || String(session.status).toLowerCase() !== 'completed' || session.voided_at) {
    return;
  }

  const { data: rows, error: itemsErr } = await supabase
    .from('count_items')
    .select('id, inventory_item_id, location, actual_quantity, item_snapshot, snapshot_at_close, pl_category_snapshot')
    .eq('count_session_id', sessionId);

  if (itemsErr) throw itemsErr;
  if (!rows?.length) return;

  // A row needs work if either the at-close snapshot or the P&L label is missing.
  const need = rows.filter((r) => r.snapshot_at_close == null || r.pl_category_snapshot == null);
  if (!need.length) return;

  const ids = [...new Set(need.map((r) => r.inventory_item_id).filter(Boolean))];
  const { data: invRows } = await supabase
    .from('inventory_items')
    .select('*')
    .in('id', ids);

  const invById = {};
  (invRows || []).forEach((inv) => {
    invById[inv.id] = inv;
  });

  // Resolve the store's P&L map once so we can freeze a label per row. If the
  // store has no mappings this returns an empty map and we leave the column
  // NULL, which the UI renders as "Unmapped".
  let plMap = null;
  try {
    plMap = await getPLCategoryMap(supabase, session.store_id);
  } catch (err) {
    console.warn('[freezeSnapshots] P&L map load failed; leaving pl_category_snapshot NULL:', err);
  }

  for (const row of need) {
    const inv = invById[row.inventory_item_id] || {};
    const update = {};
    if (row.snapshot_at_close == null) {
      const snap = buildSnapshotAtClose(row, inv);
      update.snapshot_at_close = snap;
      // Mirror the valuation fields into the dedicated count_items columns.
      // Only done when the close snapshot is first written — rows that already
      // have snapshot_at_close are historical and must never be recomputed.
      update.count_unit_snapshot = snap.count_unit ?? null;
      update.normalized_unit_cost_snapshot = snap.normalized_unit_cost ?? null;
      update.price_source_snapshot = snap.price_source ?? null;
      update.price_source_date_snapshot = snap.price_source_date ?? null;
      update.extended_value_snapshot = snap.extended_value ?? null;
    }
    if (row.pl_category_snapshot == null) {
      const sub =
        (update.snapshot_at_close && update.snapshot_at_close.category) ||
        row.item_snapshot?.category ||
        inv.category ||
        '';
      const resolved = resolvePLCategoryFromMap(sub, plMap);
      // Only persist real resolutions; NULL preserves the "Unmapped" semantics
      // so a later mapping edit doesn't require re-freezing historical rows.
      if (resolved && resolved !== 'Unmapped') {
        update.pl_category_snapshot = resolved;
      }
    }
    if (Object.keys(update).length === 0) continue;
    const { error: updateErr } = await supabase
      .from('count_items')
      .update(update)
      .eq('id', row.id);
    if (updateErr) throw updateErr;
  }
}

/**
 * Category value totals for a set of count_items rows — the same per-row math
 * as the Inventory Value report (Reports.jsx generateValueReport): frozen
 * close snapshot first, quantity × unitValuePriceFromCountItem, blank counts
 * contribute 0, category falls back to 'Uncategorized', sorted by value
 * descending. Used by the EOP completion summary so the totals shown at
 * completion always equal the report opened a click later. Rows must carry
 * actual_quantity, item_snapshot and snapshot_at_close.
 */
export function summarizeCountValueByCategory(rows = []) {
  const categoryMap = new Map();
  let totalValue = 0;
  (rows || []).forEach((row) => {
    const snap = reportingSnapshotFromCountItem(row);
    const category = snap.category || 'Uncategorized';
    const quantity = parseFloat(row?.actual_quantity) || 0;
    const itemValue = quantity * unitValuePriceFromCountItem(row);
    totalValue += itemValue;
    categoryMap.set(category, (categoryMap.get(category) || 0) + itemValue);
  });
  return {
    categories: [...categoryMap.entries()]
      .map(([category, value]) => ({ category, value }))
      .sort((a, b) => b.value - a.value),
    totalValue,
  };
}

/**
 * The valuation rule, shown verbatim on reports that present inventory values.
 */
export function valuationRuleText() {
  return "Inventory values use each item's most recent verified purchase price, converted to its count unit.";
}

/**
 * Presentation-ready view of a snapshot's valuation: the unit cost applied,
 * where it came from, when it took effect, and whether the item has no usable
 * price at all. Legacy snapshots (no normalized_unit_cost key) fall back to
 * the legacy case/each price keys so historical rows still describe correctly.
 * A missing price is reported as { unitCost: null, missing: true } — never 0.
 */
export function describeValuation(snapshot) {
  const snap = normalizeSnapshotPrices(snapshot);
  const normalized = parseFloat(snap.normalized_unit_cost);
  if (Number.isFinite(normalized) && normalized > 0) {
    return {
      unitCost: normalized,
      source: snap.price_source || 'verified',
      sourceDate: snap.price_source_date ?? null,
      missing: false,
    };
  }
  const legacy = unitValuePriceFromSnapshot(snap);
  if (legacy > 0) {
    return {
      unitCost: legacy,
      source: snap.price_source || 'legacy_item_price',
      sourceDate: snap.price_source_date ?? null,
      missing: false,
    };
  }
  return {
    unitCost: null,
    source: snap.price_source || 'missing',
    sourceDate: snap.price_source_date ?? null,
    missing: true,
  };
}
